import { InteractionEvent } from '@logto/schemas';
import { useCallback } from 'react';

import { fulfillProfile } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import useToast from '@/hooks/use-toast';
import { type ContinueFlowInteractionEvent } from '@/types';

export const useSetExtraProfile = (
  interactionEvent: ContinueFlowInteractionEvent = InteractionEvent.Register
) => {
  const asyncAddProfile = useApi(fulfillProfile);
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const { setToast } = useToast();

  const submitErrorHandlers = useSubmitInteractionErrorHandler(interactionEvent);

  const onSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      console.log({ values });

      try {
        const response = await fetch('https://students.mofad.ly/api/verify-student', {
          method: 'POST',
          headers: {
            accept: '*/*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        // Only show toast if response is not successful
        if (!response.ok) {
          try {
            const responseData = await response.json();

            // Check for specific 429 throttling error
            if (
              response.status === 429 &&
              responseData &&
              typeof responseData === 'object' &&
              'message' in responseData &&
              typeof responseData.message === 'string' &&
              responseData.message.includes('ThrottlerException: Too Many Requests')
            ) {
              setToast('تم تجاوز الحد الأقصى للطلبات، يرجى المحاولة مرة أخرى بعد قليل');
              return;
            }

            // Type guard to ensure responseData has userMessage property
            const userMessage =
              responseData &&
              typeof responseData === 'object' &&
              'userMessage' in responseData &&
              typeof responseData.userMessage === 'string'
                ? responseData.userMessage
                : 'الرجاء التأكد من صحة البيانات المدخلة';

            setToast(userMessage);
          } catch {
            // Response is not JSON, use default error message
            setToast('حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى');
          }

          return;
        }

        // If we get here, response was successful, continue with profile fulfillment
        const [error, result] = await asyncAddProfile(
          { type: 'extraProfile', values },
          interactionEvent
        );

        if (error) {
          await handleError(error, submitErrorHandlers);
          return;
        }

        if (result?.redirectTo) {
          await redirectTo(result.redirectTo);
        }
      } catch {
        // Network error or server is down
        setToast('حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى');
      }
    },
    [asyncAddProfile, submitErrorHandlers, handleError, interactionEvent, redirectTo]
  );

  return onSubmit;
};
